import React, { useState } from "react";
import FileUpload from "../generic/FileUpload";
import GenericTable from "../generic/GenericTable";
import UploadButton from "../generic/UploadButton";
import { Messages } from "../../constants/Messages";
import SharedAlertBanner from "./SharedAlertBanner";
import { BULK_TABS } from "../../constants/TabTypes";
import { CLASS_NAME } from "../../constants/ClassName";

const TABS = BULK_TABS;

type TabType = keyof typeof TABS;

interface InputMethodTabsProps {
    activeTab: typeof TABS[TabType];
    onTabChange: (tab: typeof TABS[TabType]) => void;
    onDataParsed: (data: any[]) => void;
    onSubmit: () => void;
    isLoading: boolean;
    parsedData: any[];
    requiredFields: string[]; // Required fields for field mapping
    optionalFields?: string[]; // Optional fields for field mapping
    onFieldMappingChange: (updatedMappings: Record<string, string>) => void; // Callback for field mappings
    fieldMappings: Record<string, string>; // Field mappings for FileUpload
}

const InputMethodTabs: React.FC<InputMethodTabsProps> = ({
                                                             activeTab,
                                                             onTabChange,
                                                             onDataParsed,
                                                             onSubmit,
                                                             isLoading,
                                                             parsedData,
                                                             requiredFields,
                                                             optionalFields = [],
                                                             fieldMappings,
                                                             onFieldMappingChange,
                                                         }) => {
    const [ textData, setTextData ] = useState<string>("");
    const [ successAlert, setSuccessAlert ] = useState<{ message: string } | null>(null);
    const [ errorAlert, setErrorAlert ] = useState<{ message: string } | null>(null);
    const [ currentPage, setCurrentPage ] = useState(1);

    const handleTextPreview = () => {
        try {
            const lines = textData.trim().split("\n");
            const headerRow = lines[0].split(",").map((header) => header.trim());
            const rows = lines.slice(1).map((line) =>
                line.split(",").reduce((acc, value, index) => {
                    acc[headerRow[index]] = value.trim();
                    return acc;
                }, {} as Record<string, string>),
            );
            onDataParsed(rows);
            setCurrentPage(1);
            setSuccessAlert({ message: Messages.SUCCESS_PARSE_DATA });
        } catch (error) {
            setErrorAlert({ message: Messages.ERROR_PARSE_FILE });
        }
    };

    // Create a mapped table header based on required and optional fields
    const mappedHeaders = [ ...requiredFields, ...optionalFields ];

    // Filter parsed data to include only fields from requiredFields and optionalFields
    const filteredParsedData = parsedData.map((row) =>
        mappedHeaders.reduce((acc, field) => {
            acc[field] = row[field] || ""; // Use empty string for missing fields
            return acc;
        }, {} as Record<string, any>),
    );

    return (
        <div className="w-full sm:px-2 px-8">
            <div
                className="relative flex w-full max-w-md mx-auto mb-6 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg shadow-md overflow-hidden">
                {/* Sliding background (half width). Moves on activeTab change */}
                <div
                    className={`absolute top-0 left-0 h-full w-1/2 rounded-lg bg-blue-500 transition-transform duration-300
        ${activeTab === TABS.TEXT_INPUT ? "translate-x-full" : "translate-x-0"}`}
                />

                {/* File Upload Button */}
                <button
                    onClick={() => onTabChange(TABS.FILE_UPLOAD)}
                    className={`flex-1 z-10 text-center text-sm font-medium transition-colors duration-300 focus:outline-none focus:ring-0
                    ${activeTab === TABS.FILE_UPLOAD
                        ? "text-white"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                >
                    {TABS.FILE_UPLOAD}
                </button>

                {/* Text Input Button */}
                <button
                    onClick={() => onTabChange(TABS.TEXT_INPUT)}
                    className={`flex-1 z-10 text-center text-sm font-medium transition-colors duration-300 focus:outline-none focus:ring-0
                    ${activeTab === TABS.TEXT_INPUT
                        ? "text-white"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                >
                    {TABS.TEXT_INPUT}
                </button>
            </div>

            <div className="mt-8">
                {/* Shared Alert Banner */}
                <SharedAlertBanner
                    successAlert={successAlert}
                    errorAlert={errorAlert}
                    onCloseSuccess={() => setSuccessAlert(null)}
                    onCloseError={() => setErrorAlert(null)}
                />

                {/* File Upload Section */}
                {activeTab === TABS.FILE_UPLOAD ? (
                    <div>
                        <FileUpload
                            onDataParsed={onDataParsed}
                            accept=".csv, .xlsx"
                            fieldMappings={fieldMappings}
                            requiredFields={requiredFields}
                            optionalFields={optionalFields}
                            onFieldMappingChange={onFieldMappingChange}
                        />
                        <div className="mt-6">
                            <GenericTable
                                tableTitle={"Data Preview"}
                                columns={mappedHeaders}
                                data={filteredParsedData}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </div>
                ) : (
                    /* Text Input Section */
                    <div className="flex flex-col gap-4">
                        <textarea
                            className={`w-full h-48 ${CLASS_NAME.formBorder} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-300 text-sm sm:text-base`}
                            value={textData}
                            onChange={(e) => setTextData(e.target.value)}
                            placeholder="Please paste your file content in CSV format"
                        />
                        <button className={"primary"} onClick={handleTextPreview}>Preview</button>
                        <div className="mt-6">
                            <GenericTable
                                tableTitle={"Data Preview"}
                                columns={mappedHeaders}
                                data={filteredParsedData}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </div>
                )}

                {/* Submit Button */}
                <UploadButton
                    isLoading={isLoading}
                    disabled={!parsedData.length || isLoading}
                    onClick={onSubmit}
                />
            </div>
        </div>
    );
};

export default InputMethodTabs;